import React from 'react';

export interface FormattedTextProps {
  text?: string | null;
  className?: string;
}

/**
 * Parsea fragmentos en negrita (**texto**) dentro de una línea sin usar dangerouslySetInnerHTML.
 */
export const parseInlineFormatting = (line: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      parts.push(line.substring(lastIndex, match.index));
    }
    parts.push(
      <strong key={match.index} className="font-bold text-slate-900 dark:text-white">
        {match[1]}
      </strong>
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < line.length) {
    parts.push(line.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [line];
};

/**
 * Componente semántico React que renderiza texto multilínea con formato enriquecido liviano:
 * - Párrafos separados por saltos dobles (\n\n)
 * - Viñetas (- o *) dentro de listas no ordenadas
 * - Negrita (**texto**)
 * 100% seguro contra XSS (sin dangerouslySetInnerHTML).
 */
export const FormattedText: React.FC<FormattedTextProps> = ({ text, className = '' }) => {
  if (!text || !text.trim()) {
    return null;
  }

  const blocks = text.split(/\n\s*\n/);

  return (
    <div className={`space-y-2 text-sm leading-relaxed ${className}`}>
      {blocks.map((block, blockIdx) => {
        const rawLines = block.split('\n');
        const lines = rawLines.map((l) => l.trim()).filter((l) => l.length > 0);
        if (lines.length === 0) return null;

        const isList = lines.some((l) => l.startsWith('- ') || l.startsWith('* '));

        if (isList) {
          return (
            <ul key={blockIdx} className="list-disc list-inside space-y-1 my-1 pl-1">
              {lines.map((line, lineIdx) => {
                const isBullet = line.startsWith('- ') || line.startsWith('* ');
                const content = isBullet ? line.substring(2) : line;
                return (
                  <li key={lineIdx} className={isBullet ? '' : 'list-none pl-3'}>
                    {parseInlineFormatting(content)}
                  </li>
                );
              })}
            </ul>
          );
        }

        return (
          <p key={blockIdx}>
            {lines.map((line, lineIdx) => (
              <React.Fragment key={lineIdx}>
                {parseInlineFormatting(line)}
                {lineIdx < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export default FormattedText;
